import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';

import _ from 'lodash';

import { getConditionalObject } from 'utils/data';

import { showMessageAction } from 'store/global/actions';

import { generateFinancialInfoPath } from '../../FinancialInfo.utils';
import { SAVE_BLOCKED_GENERIC_MESSAGE } from '../UpdateTableModal.constants';
import {
  getFirstInvalidRecordId,
  getRecordDisplayName,
  getSaveBlockedMessage,
  getSaveBlockedTarget,
} from '../UpdateTableModal.utils';

const useSaveBlockedHandler = ({
  applicationId,
  tab,
  tablePath,
  actionType,
  recordId,
  borrowerId,
  data,
  allVisibleRecords,
  allRecords,
  isClosedHidden,
  isAllBorrowersTabVisible,
  hiddenPageIds,
  hiddenFieldIds,
  isNestedEntityEditActive,
  pathFromRef,
}) => {
  const history = useHistory();
  const dispatch = useDispatch();

  return useCallback(
    payload => {
      if (!payload.length) {
        dispatch(showMessageAction({ message: SAVE_BLOCKED_GENERIC_MESSAGE, variant: 'error' }));

        return;
      }

      const targetId = getFirstInvalidRecordId({
        orderedRecordLists: [data, allVisibleRecords, allRecords],
        invalidRecordIds: _.uniq(payload.map(({ recordId }) => recordId)),
      });

      const target = getSaveBlockedTarget({
        targetId,
        borrowerId,
        currentData: data,
        allVisibleRecords,
        allRecords,
        isClosedHidden,
        isAllBorrowersTabVisible,
        hiddenPageIds,
        hiddenFieldIds,
        isNestedEntityEditActive,
      });

      if (target && (targetId !== recordId || target.borrowerId !== borrowerId)) {
        const pathFrom = pathFromRef.current;

        history.push({
          ...generateFinancialInfoPath({
            applicationId,
            tab,
            tablePath,
            borrowerId: target.borrowerId,
            actionType,
            recordId: targetId,
            pathFrom,
          }),
          state: {
            pathFrom,
            ...getConditionalObject(target.reveal, { revealedRecordId: targetId }),
          },
        });
      }

      const targetRecord = allRecords.find(({ id }) => id === targetId);

      dispatch(
        showMessageAction({
          message: getSaveBlockedMessage({
            tablePath,
            payload,
            recordName: targetRecord
              ? getRecordDisplayName({ tablePath, record: targetRecord })
              : null,
            isTargetShown: !!target,
          }),
          variant: 'error',
        }),
      );
    },
    [
      applicationId,
      tab,
      tablePath,
      actionType,
      recordId,
      borrowerId,
      data,
      allVisibleRecords,
      allRecords,
      isClosedHidden,
      isAllBorrowersTabVisible,
      hiddenPageIds,
      hiddenFieldIds,
      isNestedEntityEditActive,
      pathFromRef,
      history,
      dispatch,
    ],
  );
};

export default useSaveBlockedHandler;
